import { useMemo, useState } from 'react'
import { isEmpty } from 'lodash'
import { Grid, TableCell, TableRow, TextField } from '@mui/material'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import Content from 'components/content/content'
import { useGetUsersQuery } from 'store/userMember'
import type { UserMember } from 'modules/types'
import ActionMenu from 'components/actionMenu'
import GLOBAL from 'modules/global'
import Drawer from 'components/drawer/detail'
import Filter from 'components/filter/filter'
import UserDetail from './detail'

export default function UserListPage() {
    const columns = useMemo(
        () => [
            { name: 'index', render: (_: unknown, index: number) => index + 1 },
            {
                name: 'actions',
                render: (user: UserMember) => (
                    <ActionMenu
                        id={`inbox-${user.userId}`}
                        menus={[
                            {
                                label: 'View',
                                onClick: () => setViewId(user.userId)
                            }
                        ]}
                    />
                )
            },
            {
                name: 'Member ID',
                accessor: 'memberId'
            },
            // fullName, email, tier, vin, phoneNumber, coinsBalance, birthday, city, province, status
            {
                name: 'Full Name',
                accessor: 'fullName'
            },
            {
                name: 'Email',
                accessor: 'email'
            },
            {
                name: 'Tier',
                accessor: 'memberTier'
            },
            {
                name: 'VIN',
                accessor: 'vin',
                render: (user: UserMember) => <VinColumn vin={user.vin} />
            },
            {
                name: 'Phone Number',
                accessor: 'phoneNumber'
            },
            {
                name: 'Coins Balance',
                accessor: 'coins',
                render: (user: UserMember) => GLOBAL.numberFormat(user.coins)
            },
            {
                name: 'Birthday',
                accessor: 'dateOfBirth',
                render: (user: UserMember) => dayjs(user.dateOfBirth).format('DD-MM-YYYY')
            },
            {
                name: 'City',
                accessor: 'city'
            },
            {
                name: 'Province',
                accessor: 'province'
            },
            {
                name: 'Status',
                accessor: 'status'
            }
        ],
        []
    )

    const dateFilter = useMemo(
        () => [
            { value: 'minCreatedDate', label: 'Min Created Date', id: 'min-created-date' },
            { value: 'maxCreatedDate', label: 'Max Created Date', id: 'max-created-date' },
            { value: 'minBirthdate', label: 'Min Birthdate', id: 'min-birthdate' },
            { value: 'maxBirthdate', label: 'Max Birthdate', id: 'max-birthdate' },
            { value: 'minLastLogin', label: 'Min Last Login Date', id: 'min-last-login' },
            { value: 'maxLastLogin', label: 'Max Last Login Date', id: 'max-last-login' },
            { value: 'minSignup', label: 'Min Signup Date', id: 'min-signup' },
            { value: 'maxSignup', label: 'Max Signup Date', id: 'max-signup' }
        ],
        []
    )

    const [viewId, setViewId] = useState<string | null>(null)
    const [payload, setPayload] = useState({
        start: 0,
        length: 10,
        field: '',
        search: { value: '' },
        minCreatedDate: '',
        maxCreatedDate: '',
        minBirthdate: '',
        maxBirthdate: '',
        memberTier: '',
        latestRegion: '',
        status: '',
        idmStatus: '',
        cityId: '',
        provinceId: '',
        carModelId: '',
        osName: '',
        minLastLogin: '',
        maxLastLogin: '',
        minSignup: '',
        maxSignup: '',
        minCoins: '',
        maxCoins: '',
        vin: ''
    })
    const [isFiltering, setFiltering] = useState(false)
    const [filter, setFilter] = useState({
        minCreatedDate: '',
        maxCreatedDate: '',
        minBirthdate: '',
        maxBirthdate: '',
        minLastLogin: '',
        maxLastLogin: '',
        minSignup: '',
        maxSignup: '',
        minCoins: '',
        maxCoins: '',
        vin: ''
    })
    const { data, isLoading, isFetching } = useGetUsersQuery({ ...payload })
    const users = data?.data || []

    function onChangePage(value: number) {
        setPayload((prev) => ({ ...prev, start: value - 1 }))
    }

    function onChangePerPage(value: number) {
        setPayload((prev) => ({ ...prev, length: value, start: 0 }))
    }

    function handleReset() {
        const newState = {
            maxCreatedDate: '',
            minCreatedDate: '',
            maxBirthdate: '',
            minBirthdate: '',
            minLastLogin: '',
            maxLastLogin: '',
            minSignup: '',
            maxSignup: '',
            minCoins: '',
            maxCoins: '',
            vin: ''
        }
        setFilter(newState)
        setPayload({ ...payload, start: 0, ...newState })
        setFiltering(false)
    }

    const handleChangeDate = (key: string) => (date: Dayjs | null) => {
        const newDate = (!isEmpty(date) && date?.format('YYYY-MM-DD')) || ''
        setFilter({ ...filter, [key]: newDate })
    }

    function handleSubmitFilter() {
        setPayload({ ...payload, start: 0, ...filter })
        setFiltering(false)
    }

    function onSearch(value: string) {
        return setPayload({ ...payload, start: 0, search: { value } })
    }

    return (
        <>
            <Content
                isLoading={isLoading}
                isFetching={isFetching}
                title='User List'
                tableHeadTitles={columns.slice(2).map((col) => col.name)}
                useNoAction
                onSearch={onSearch}
                // onResync={onResync}
                onFilter={() => setFiltering(true)}
                // onDownload={onDownload}
                onDownloadTypeIgnored={['pdf']}
                // downloadLoading={download.isLoading}
                // onUpload={onCreate}
                // uploadTitle='Create New Push Notification'
                // uploadPrefixIcon='Add'
                // onAdditional={listChecked.length ? onSchedule : undefined}
                additionalTitle='On Schedule'
                additionalColor='success'
                // additionalLoading={updateSchedule.isLoading}
                pagination={onChangePage}
                onChangePerPage={onChangePerPage}
                totalPage={data && GLOBAL.tableTotalPage(data?.recordsTotal)}
                totalRecords={data?.recordsTotal || 0}
                page={payload.start + 1}
                // isLoading={listPushNotification.isLoading}
                useDownloadDropdown
            // useCheckbox
            // onCheckBoxAll={(e) => handleCheckAll(e)}
            // allChecked={listChecked}
            >
                {users.map((user, i) => (
                    <TableRow key={user.userId}>
                        {columns.map((col) => (
                            <TableCell key={col.accessor}>
                                {col.render ? col.render(user, i) : user[col.accessor as keyof typeof user]}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </Content>

            <Filter
                open={isFiltering}
                title='Filter'
                onClose={() => setFiltering(false)}
                onCancel={() => setFiltering(false)}
                onSubmit={handleSubmitFilter}
                isSubmitDisabled={!GLOBAL.isFilterContainValue(filter)}
                onReset={handleReset}
            >
                <Grid container spacing={2} columns={12}>
                    {dateFilter.map((item) => (
                        <Grid item xs={6} key={item.id}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                                <DatePicker
                                    key={item.value}
                                    onChange={handleChangeDate(item.value)}
                                    label={item.label}
                                    value={filter[item.value as never] ? dayjs(filter[item.value as never]) : null}
                                    sx={{ width: '100%' }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    ))}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            variant='outlined'
                            label='Min Coin'
                            type='number'
                            value={filter.minCoins}
                            onChange={(event) => setFilter({ ...filter, minCoins: event.target.value })}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            variant='outlined'
                            label='Max Coin'
                            type='number'
                            value={filter.maxCoins}
                            onChange={(event) => setFilter({ ...filter, maxCoins: event.target.value })}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            variant='outlined'
                            label='VIN'
                            value={filter.vin}
                            onChange={(event) => setFilter({ ...filter, vin: event.target.value })}
                        />
                    </Grid>
                </Grid>
            </Filter>
            <UserDetail id={viewId} open={!!viewId} onClose={() => setViewId(null)} />
        </>
    )
}

function VinColumn({ vin }: { vin: string }) {
    const [open, setOpen] = useState(false)
    const vins = vin?.split(';') || []

    return (
        <div>
            {vins.slice(0, 3).join(';')}{' '}
            {vins.length > 3 ? (
                <a href='#' title={vins.slice(3).join(', ')} onClick={() => setOpen(true)}>
                    ...more
                </a>
            ) : (
                ''
            )}
            <Drawer title='Vin Detail' open={open} onClose={() => setOpen(false)} maxWidth='sm'>
                <Grid container spacing={2} sx={{ mt: 0.5, p: 3 }}>
                    {vins
                        .filter((v) => !!v.length)
                        .map((vin, i) => (
                            <Grid item xs={12} key={vin}>
                                <TextField
                                    label={`VIN ${i + 1}`}
                                    value={vin}
                                    fullWidth
                                    inputProps={{ readOnly: true }}
                                />
                            </Grid>
                        ))}
                </Grid>
            </Drawer>
        </div>
    )
}
